import { useEffect, useState } from "react";
import api from "../../api";
import { Spinner } from "../../components/Logo";
import { PageHeader, StatCard } from "../../components/ui";
import { Users, UserCheck, BookOpen, Flag, Award, Zap, Activity, Layers } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, AreaChart, Area } from "recharts";

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <Spinner />;
  const k = d.kpis;

  return (
    <div data-testid="admin-dashboard">
      <PageHeader overline="Super Admin" title="Platform overview" desc="Live snapshot of users, content and engagement." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Users} label="Total learners" value={k.total_users} testid="kpi-users" />
        <StatCard icon={UserCheck} label="Active" value={k.active_users} accent="#10B981" />
        <StatCard icon={Activity} label="Active today" value={k.dau} accent="#0ea5e9" />
        <StatCard icon={Zap} label="Weekly active" value={k.wau} accent="#a855f7" />
        <StatCard icon={BookOpen} label="Learning paths" value={k.paths} accent="#F59E0B" />
        <StatCard icon={Layers} label="Modules" value={k.modules} accent="#0ea5e9" />
        <StatCard icon={Flag} label="CTF challenges" value={k.ctfs} accent="#10B981" />
        <StatCard icon={Award} label="Certificates" value={k.certificates} accent="#F26522" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <div className="card p-6">
          <div className="overline mb-4">Registrations (30 days)</div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={d.registrations}>
              <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F26522" stopOpacity={0.4} /><stop offset="100%" stopColor="#F26522" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="day" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#121C30", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
              <Area type="monotone" dataKey="count" stroke="#F26522" fill="url(#g1)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="card p-6">
          <div className="overline mb-4">Certificates by path</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.completions}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="title" stroke="#64748B" fontSize={10} tickFormatter={(v) => v.length > 12 ? v.slice(0, 12) + "…" : v} />
              <YAxis stroke="#64748B" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#121C30", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
              <Bar dataKey="certificates" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card p-6">
        <div className="overline mb-4">Recent activity</div>
        {d.recent_activity.length === 0 ? <p className="text-slate-500 text-sm">No recent activity.</p> : (
          <div className="space-y-2">
            {d.recent_activity.map((a, i) => (
              <div key={i} className="flex items-center justify-between text-sm py-2 border-b border-white/5 last:border-0">
                <span className="text-slate-300"><span className="text-[#38bdf8]">{a.actor_email}</span> · {a.action.replace(/_/g, " ")} {a.entity_type && <span className="text-slate-500">({a.entity_type})</span>}</span>
                <span className="text-slate-500 text-xs">{new Date(a.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
